import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { C, S } from '../../../utils/styles';
import Icon from '../../Icon';
import { useToast } from '../../Toast';
import {
  listCourses,
  createCourse,
  archiveCourse,
  deleteCourse,
  AUDIENCE_OPTIONS,
  STATUS_OPTIONS,
  audienceLabel,
  statusLabel,
} from './api';
import CourseBuilder from './CourseBuilder';

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return '—';
  }
}

function statusBadgeStyle(status) {
  if (status === 'published') {
    return { background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}` };
  }
  if (status === 'archived') {
    return { background: C.redLight, color: C.red, border: `1px solid ${C.red}33` };
  }
  return { background: C.bg, color: C.textMid, border: `1px solid ${C.border}` };
}

function audienceBadgeStyle(audience) {
  if (audience === 'account_manager') return { background: C.accentBg, color: C.accent };
  if (audience === 'employer') return { background: C.purpleLight, color: C.purple };
  if (audience === 'employee') return { background: C.orangeLight, color: C.orange };
  return { background: C.bg, color: C.textMid };
}

export default function CoursesListEditor() {
  const showToast = useToast();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audienceFilter, setAudienceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingId, setEditingId] = useState(null); // null = list view
  const [showCreate, setShowCreate] = useState(false);

  // New-course form state
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newAudience, setNewAudience] = useState('account_manager');
  const [newMustComplete, setNewMustComplete] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCourses({
        audience: audienceFilter || undefined,
        status: statusFilter || undefined,
      });
      setCourses(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast(err.message || 'Failed to load courses', 'error');
    } finally {
      setLoading(false);
    }
  }, [audienceFilter, statusFilter, showToast]);

  useEffect(() => {
    if (!editingId) {
      loadCourses();
    }
  }, [editingId, loadCourses]);

  const filteredCount = courses.length;

  const stats = useMemo(() => {
    const counts = { draft: 0, published: 0, archived: 0 };
    for (const c of courses) {
      if (counts[c.status] !== undefined) counts[c.status] += 1;
    }
    return counts;
  }, [courses]);

  const resetNewForm = () => {
    setNewTitle('');
    setNewDescription('');
    setNewAudience('account_manager');
    setNewMustComplete(false);
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!newTitle.trim()) {
      showToast('Course title is required', 'error');
      return;
    }
    setCreating(true);
    try {
      const created = await createCourse({
        title: newTitle.trim(),
        description: newDescription.trim(),
        audience: newAudience,
        mustCompleteBeforeAccess: newMustComplete,
      });
      showToast(`Course "${created.title}" created as draft`, 'success');
      resetNewForm();
      setShowCreate(false);
      setEditingId(created.id); // jump straight into the builder
    } catch (err) {
      showToast(err.message || 'Failed to create course', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleArchive = async (course) => {
    if (!window.confirm(`Archive "${course.title}"? It will no longer be visible to learners but you can re-publish later.`)) {
      return;
    }
    try {
      await archiveCourse(course.id);
      showToast('Course archived', 'success');
      loadCourses();
    } catch (err) {
      showToast(err.message || 'Failed to archive course', 'error');
    }
  };

  const handleDelete = async (course) => {
    if (!window.confirm(`Permanently delete "${course.title}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteCourse(course.id);
      showToast('Course deleted', 'success');
      loadCourses();
    } catch (err) {
      showToast(err.message || 'Failed to delete course', 'error');
    }
  };

  if (editingId) {
    return (
      <CourseBuilder
        courseId={editingId}
        onBack={() => setEditingId(null)}
        onChanged={() => {
          // Optimistically refresh list when user comes back
        }}
      />
    );
  }

  return (
    <div data-testid="courses-list-editor" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Intro card */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ maxWidth: 720 }}>
            <div style={S.heading}>E-Learning — Course Builder</div>
            <p style={{ color: C.textMid, margin: 0, fontSize: 14, lineHeight: 1.6 }}>
              Build proven, role-targeted learning modules for Account Managers, Employers, and Employees.
              Each course is a sequence of lessons (rich content + interactive blocks) followed by a final
              quiz with pass/fail logic. Mark a course <strong>"Must complete before portal access"</strong>{' '}
              to gate dashboards behind successful completion (gating runtime ships in Phase 18.2).
            </p>
          </div>
          <button
            type="button"
            data-testid="new-course-btn"
            onClick={() => setShowCreate((v) => !v)}
            style={S.buttonPrimary}
          >
            <Icon name="add-outline" style={{ fontSize: 16, marginRight: 6, verticalAlign: '-2px' }} />
            New Course
          </button>
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <StatPill label="Total" value={filteredCount} color={C.text} />
          <StatPill label="Drafts" value={stats.draft} color={C.textMid} />
          <StatPill label="Published" value={stats.published} color={C.green} />
          <StatPill label="Archived" value={stats.archived} color={C.red} />
        </div>
      </div>

      {/* New-course form */}
      {showCreate && (
        <form onSubmit={handleCreate} data-testid="new-course-form" style={{ ...S.card, borderColor: C.accent, background: C.accentBg }}>
          <div style={{ ...S.heading, color: C.accent }}>Create New Course</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={S.label}>Course Title *</label>
              <input
                data-testid="new-course-title"
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. AM Onboarding 101"
                style={S.input}
                autoFocus
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={S.label}>Short Description</label>
              <textarea
                data-testid="new-course-description"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="One sentence learners will see on the course tile."
                rows={2}
                style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 500, resize: 'vertical' }}
              />
            </div>
            <div>
              <label style={S.label}>Audience *</label>
              <select
                data-testid="new-course-audience"
                value={newAudience}
                onChange={(e) => setNewAudience(e.target.value)}
                style={S.select}
              >
                {AUDIENCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label
                data-testid="new-course-must-complete-label"
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMid, fontWeight: 500, cursor: 'pointer' }}
              >
                <input
                  data-testid="new-course-must-complete"
                  type="checkbox"
                  checked={newMustComplete}
                  onChange={(e) => setNewMustComplete(e.target.checked)}
                />
                Must complete before portal access
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button
              type="button"
              data-testid="new-course-cancel"
              onClick={() => { setShowCreate(false); resetNewForm(); }}
              style={S.buttonSecondary}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="new-course-submit"
              disabled={creating}
              style={{ ...S.buttonPrimary, opacity: creating ? 0.6 : 1 }}
            >
              {creating ? 'Creating...' : 'Create & Open Builder'}
            </button>
          </div>
        </form>
      )}

      {/* Filters + list */}
      <div style={S.card}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ flex: '0 0 200px' }}>
            <label style={S.label}>Audience</label>
            <select
              data-testid="filter-audience"
              value={audienceFilter}
              onChange={(e) => setAudienceFilter(e.target.value)}
              style={S.select}
            >
              <option value="">All audiences</option>
              {AUDIENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <label style={S.label}>Status</label>
            <select
              data-testid="filter-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={S.select}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            data-testid="filter-reset"
            onClick={() => { setAudienceFilter(''); setStatusFilter(''); }}
            style={{ ...S.buttonSecondary, padding: '8px 14px' }}
          >
            Reset
          </button>
          <button
            type="button"
            data-testid="filter-refresh"
            onClick={loadCourses}
            style={{ ...S.buttonSecondary, padding: '8px 14px' }}
          >
            <Icon name="refresh-outline" style={{ fontSize: 14, marginRight: 4, verticalAlign: '-1px' }} />
            Refresh
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={S.th}>Title</th>
                <th style={S.th}>Audience</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Gating</th>
                <th style={S.th}>Lessons</th>
                <th style={S.th}>Quiz Q's</th>
                <th style={S.th}>Updated</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={8}>
                    Loading courses...
                  </td>
                </tr>
              )}
              {!loading && courses.length === 0 && (
                <tr>
                  <td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={8}>
                    No courses match the current filters. Click <strong>+ New Course</strong> to create the first one.
                  </td>
                </tr>
              )}
              {!loading && courses.map((c) => (
                <tr key={c.id} data-testid={`course-row-${c.id}`}>
                  <td style={S.td}>
                    <button
                      type="button"
                      data-testid={`course-title-${c.id}`}
                      onClick={() => setEditingId(c.id)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color: C.accent,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {c.title}
                    </button>
                    {c.description ? (
                      <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
                        {c.description.length > 90 ? `${c.description.slice(0, 90)}...` : c.description}
                      </div>
                    ) : null}
                  </td>
                  <td style={S.td}>
                    <span style={{ ...badgeBase, ...audienceBadgeStyle(c.audience) }}>
                      {audienceLabel(c.audience)}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={{ ...badgeBase, ...statusBadgeStyle(c.status) }}>
                      {statusLabel(c.status)}{c.status === 'published' ? ` · v${c.version || 1}` : ''}
                    </span>
                  </td>
                  <td style={S.td}>
                    {c.mustCompleteBeforeAccess ? (
                      <span style={{ ...badgeBase, background: C.orangeLight, color: C.orange }}>Mandatory</span>
                    ) : (
                      <span style={{ color: C.textLight, fontSize: 12 }}>Optional</span>
                    )}
                  </td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{(c.lessons || []).length}</td>
                  <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{((c.quiz || {}).questions || []).length}</td>
                  <td style={S.td}>{formatDate(c.updatedAt)}</td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      data-testid={`edit-course-${c.id}`}
                      onClick={() => setEditingId(c.id)}
                      style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}
                    >
                      Edit
                    </button>
                    {c.status !== 'archived' && (
                      <button
                        type="button"
                        data-testid={`archive-course-${c.id}`}
                        onClick={() => handleArchive(c)}
                        style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}
                      >
                        Archive
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`delete-course-${c.id}`}
                      onClick={() => handleDelete(c)}
                      style={{ ...S.buttonDanger, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const badgeBase = {
  display: 'inline-block',
  padding: '3px 8px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.02em',
};

function StatPill({ label, value, color }) {
  return (
    <div
      style={{
        padding: '8px 14px',
        borderRadius: 10,
        background: C.bg,
        border: `1px solid ${C.border}`,
        minWidth: 88,
      }}
    >
      <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace" }}>
        {value}
      </div>
    </div>
  );
}
